import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Pagination,
  Stack,
  Avatar,
  Divider,
  MenuItem
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  SmartToy as AIIcon,
  Message as MessageIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import AudioPlayer from './AudioPlayer';
import logo from '../assets/logo.png';
import agentImg from '../assets/agent.png';

const AdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [user, setUser] = useState(null);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateAdminUser, setShowCreateAdminUser] = useState(false);
  const [newAdminUserData, setNewAdminUserData] = useState({ username: '', password: '', role: 'admin' });
  const [showUpdateAdminUser, setShowUpdateAdminUser] = useState(false);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [updateAdminUserData, setUpdateAdminUserData] = useState({ username: '', password: '', role: 'admin' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersLoading, setUsersLoading] = useState(false);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [conversationsTotalPages, setConversationsTotalPages] = useState(1);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // Chat history dialog state
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [selectedUserChats, setSelectedUserChats] = useState([]);
  const [selectedUserPhone, setSelectedUserPhone] = useState('');
  const [chatHistoryPage, setChatHistoryPage] = useState(1);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [chatScrollContainer, setChatScrollContainer] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // New state for UsersPage
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);

  // Admin users management state
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);

  const USERS_PAGE_SIZE = 10;
  const CONVERSATIONS_PAGE_SIZE = 10;

  // Check for existing session on component mount
  useEffect(() => {
    const token = localStorage.getItem('adminSessionToken');
    if (token) {
      setSessionToken(token);
      validateSession(token);
    }
  }, []);

  // Load data when page changes
  useEffect(() => {
    if (isLoggedIn && sessionToken) {
      loadData();
    }
  }, [currentPage, isLoggedIn, sessionToken]);

  const validateSession = async (token) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/statistics`, {
        headers: {
          'X-Session-Token': token
        }
      });
      
      if (response.ok) {
        setIsLoggedIn(true);
        loadData();
      } else {
        localStorage.removeItem('adminSessionToken');
        setSessionToken('');
        setIsLoggedIn(false);
      }
    } catch (error) {
      console.error('Session validation error:', error);
      localStorage.removeItem('adminSessionToken');
      setSessionToken('');
      setIsLoggedIn(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();

      if (response.ok) {
        setSessionToken(data.session_token);
        setUser(data.user);
        setIsLoggedIn(true);
        localStorage.setItem('adminSessionToken', data.session_token);
        setCurrentPage('dashboard'); // Reset to first page
        loadData();
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/api/admin/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_token: sessionToken })
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggedIn(false);
      setSessionToken('');
      setUser(null);
      setStatistics({});
      setUsers([]);
      setConversations([]);
      setUsersPage(1);
      setUsersTotalPages(1);
      setConversationsPage(1);
      setConversationsTotalPages(1);
      localStorage.removeItem('adminSessionToken');
    }
  };

  const loadData = async () => {
    if (!sessionToken) return;

    try {
      // Load statistics
      const statsResponse = await fetch(`${API_BASE}/api/admin/statistics`, {
        headers: {
          'X-Session-Token': sessionToken
        }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStatistics(statsData.statistics);
      }

      // Only load users with last messages if we're on the dashboard
      if (currentPage === 'dashboard') {
        const usersResponse = await fetch(
          `${API_BASE}/api/admin/users-with-last-message?limit=20&offset=0`,
          {
            headers: {
              'X-Session-Token': sessionToken
            }
          }
        );
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          // setUsersWithLastMessage(usersData.users); // This line is removed
        }
      }
    } catch (error) {
      console.error('Data loading error:', error);
      setError('Failed to load data');
    }
  };

  // Fetch paginated users
  const fetchUsers = useCallback(async (page = 1) => {
    setUsersLoading(true);
    try {
      // Ensure page is a valid number
      const pageNum = parseInt(page) || 1;
      const offset = (pageNum - 1) * USERS_PAGE_SIZE;
      const response = await fetch(`${API_BASE}/api/admin/users-with-last-message?limit=${USERS_PAGE_SIZE}&offset=${offset}`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setUsersTotalPages(Math.ceil((statistics.unique_users || 1) / USERS_PAGE_SIZE));
      }
    } finally {
      setUsersLoading(false);
    }
  }, [API_BASE, sessionToken, statistics.unique_users]);

  // Fetch paginated conversations (same as users-with-last-message)
  const fetchConversations = useCallback(async (page = 1) => {
    setConversationsLoading(true);
    try {
      // Ensure page is a valid number
      const pageNum = parseInt(page) || 1;
      const offset = (pageNum - 1) * CONVERSATIONS_PAGE_SIZE;
      const response = await fetch(`${API_BASE}/api/admin/users-with-last-message?limit=${CONVERSATIONS_PAGE_SIZE}&offset=${offset}`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.users);
        setConversationsTotalPages(Math.ceil((statistics.unique_users || 1) / CONVERSATIONS_PAGE_SIZE));
      }
    } finally {
      setConversationsLoading(false);
    }
  }, [API_BASE, sessionToken, statistics.unique_users]);

  // Fetch admin users
  const fetchAdminUsers = useCallback(async () => {
    setAdminUsersLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Admin users data:', data.users); // Debug log
        console.log('First admin user object:', data.users?.[0]); // Debug first user
        setAdminUsers(data.users || []);
      }
    } finally {
      setAdminUsersLoading(false);
    }
  }, [API_BASE, sessionToken]);

  // Create admin user
  const handleCreateAdminUser = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken
        },
        body: JSON.stringify(newAdminUserData)
      });
      const data = await response.json();
      if (response.ok) {
        setShowCreateAdminUser(false);
        setNewAdminUserData({ username: '', password: '', role: 'admin' });
        fetchAdminUsers();
        setError('');
      } else {
        setError(data.detail || 'Failed to create admin user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update admin user
  const handleUpdateAdminUser = async () => {
    if (!selectedAdminUser) {
      setError('No user selected for update');
      return;
    }
    
    // Try different possible ID field names
    const userId = selectedAdminUser.id || selectedAdminUser.user_id || selectedAdminUser._id || selectedAdminUser.username;
    
    if (!userId) {
      setError('Could not identify user ID for update');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken
        },
        body: JSON.stringify(updateAdminUserData)
      });
      const data = await response.json();
      if (response.ok) {
        setShowUpdateAdminUser(false);
        setSelectedAdminUser(null);
        setUpdateAdminUserData({ username: '', password: '', role: 'admin' });
        fetchAdminUsers();
        setError('');
      } else {
        setError(data.detail || 'Failed to update admin user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete admin user
  const handleDeleteAdminUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this admin user?')) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'X-Session-Token': sessionToken
        }
      });
      if (response.ok) {
        fetchAdminUsers();
        setError('');
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to delete admin user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Edit admin user
  const handleEditAdminUser = (user) => {
    console.log('Admin user object:', user); // Debug log
    setSelectedAdminUser(user);
    setUpdateAdminUserData({
      username: user.username,
      password: '',
      role: user.role || 'admin'
    });
    setShowUpdateAdminUser(true);
  };

  // Fetch chat history for a user
  const handleViewChatHistory = async (userPhone) => {
    setSelectedUserPhone(userPhone);
    setShowChatHistory(true);
    setChatHistoryPage(1);
    setHasMoreChats(true);
    setSelectedUserChats([]);
    await loadChatHistory(userPhone, 1, true);
  };

  // Load chat history with pagination
  const loadChatHistory = async (userPhone, page = 1, isInitial = false) => {
    setChatHistoryLoading(true);
    try {
      const limit = 20;
      const offset = (page - 1) * limit;
      const response = await fetch(`${API_BASE}/api/admin/chat-history?user_phone=${userPhone}&limit=${limit}&offset=${offset}`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      if (response.ok) {
        const data = await response.json();
        const newChats = data.chats || [];
        
        if (isInitial) {
          setSelectedUserChats(newChats);
        } else {
          // Append newer messages to the end of the array
          setSelectedUserChats(prev => [...prev, ...newChats]);
        }
        
        // Check if there are more messages
        setHasMoreChats(newChats.length === limit);
        setChatHistoryPage(page);
      }
    } finally {
      setChatHistoryLoading(false);
    }
  };

  // Handle scroll to load more chat history
  const handleChatScroll = useCallback(async (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when scrolled to bottom
    if (scrollTop + clientHeight >= scrollHeight - 5 && hasMoreChats && !chatHistoryLoading) {
      await loadChatHistory(chatHistoryPage);
    }
  }, [hasMoreChats, chatHistoryLoading, chatHistoryPage, loadChatHistory]);

  // Load users/conversations on page change
  useEffect(() => {
    if (currentPage === 'users' && sessionToken) fetchUsers(usersPage);
  }, [currentPage, usersPage, sessionToken, fetchUsers]);
  useEffect(() => {
    if (currentPage === 'chats' && sessionToken) fetchConversations(conversationsPage);
  }, [currentPage, conversationsPage, sessionToken, fetchConversations]);
  useEffect(() => {
    if (currentPage === 'admin-users' && sessionToken) fetchAdminUsers();
  }, [currentPage, sessionToken, fetchAdminUsers]);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // UsersPage component (now inside AdminPanel)
  const UsersPage = () => (
    <Card sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 24px rgba(4, 201, 200, 0.12)' }}>
      <CardContent>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#04C9C8' }}>Users</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User Phone</TableCell>
                <TableCell>Last Message</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersLoading ? (
                <TableRow><TableCell colSpan={4}>Loading...</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={4}>No users found.</TableCell></TableRow>
              ) : users.map((user) => (
                <TableRow key={user.user_phone}>
                  <TableCell>{user.user_phone}</TableCell>
                  <TableCell>{user.last_message}</TableCell>
                  <TableCell>{formatTimestamp(user.last_timestamp)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleViewChatHistory(user.user_phone)} sx={{ color: '#04C9C8' }}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={usersTotalPages}
            page={usersPage}
            onChange={(_, page) => setUsersPage(page)}
            color="primary"
          />
        </Box>
      </CardContent>
    </Card>
  );

  // ChatsPage component (now inside AdminPanel)
  const ChatsPage = () => (
    <Card sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 24px rgba(4, 201, 200, 0.12)' }}>
      <CardContent>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#04C9C8' }}>Conversations</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User Phone</TableCell>
                <TableCell>Last Message</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversationsLoading ? (
                <TableRow><TableCell colSpan={4}>Loading...</TableCell></TableRow>
              ) : conversations.length === 0 ? (
                <TableRow><TableCell colSpan={4}>No conversations found.</TableCell></TableRow>
              ) : conversations.map((user) => (
                <TableRow key={user.user_phone}>
                  <TableCell>{user.user_phone}</TableCell>
                  <TableCell>{user.last_message}</TableCell>
                  <TableCell>{formatTimestamp(user.last_timestamp)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleViewChatHistory(user.user_phone)} sx={{ color: '#04C9C8' }}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={conversationsTotalPages}
            page={conversationsPage}
            onChange={(_, page) => setConversationsPage(page)}
            color="primary"
          />
        </Box>
      </CardContent>
    </Card>
  );

  // UsersManagementPage component (admin users management)
  const UsersManagementPage = () => (
    <Card sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 24px rgba(4, 201, 200, 0.12)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#04C9C8' }}>Admin Users</Typography>
          <Button
            variant="contained"
            startIcon={<PersonIcon />}
            onClick={() => setShowCreateAdminUser(true)}
            sx={{
              background: '#04C9C8',
              '&:hover': { background: '#03B8B7' },
              borderRadius: 2
            }}
          >
            Add Admin User
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminUsersLoading ? (
                <TableRow><TableCell colSpan={4}>Loading...</TableCell></TableRow>
              ) : adminUsers.length === 0 ? (
                <TableRow><TableCell colSpan={4}>No admin users found.</TableCell></TableRow>
              ) : adminUsers.map((adminUser) => (
                <TableRow key={adminUser.id}>
                  <TableCell>
                    {adminUser.username || adminUser.name || adminUser.user_name || adminUser.email || 'N/A'}
                    {/* Debug: Show raw object */}
                    <Typography variant="caption" color="text.secondary" display="block">
                      Debug: {JSON.stringify(adminUser)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={adminUser.role || 'admin'} 
                      size="small" 
                      sx={{ 
                        bgcolor: adminUser.role === 'super_admin' ? '#ff9800' : '#04C9C8',
                        color: 'white'
                      }} 
                    />
                  </TableCell>
                  <TableCell>{formatTimestamp(adminUser.created_at)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        size="small" 
                        onClick={() => handleEditAdminUser(adminUser)}
                        sx={{ color: '#04C9C8' }}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => {
                          const userId = adminUser.id || adminUser.user_id || adminUser._id || adminUser.username;
                          handleDeleteAdminUser(userId);
                        }}
                        sx={{ color: '#f44336' }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          p: 2
        }}
      >       
        {/* Login Card */}
        <Paper elevation={0} sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          background: 'transparent',
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(4, 201, 200, 0.1)',
          border: '1px solid rgba(4, 201, 200, 0.1)'
        }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{
              width: 80,
              height: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              background: 'white',
              p: '5px',
              borderRadius: 2
            }}>
              <img src={logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
              Admin Panel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Login to access chat history
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Username"
            value={loginData.username}
            onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
            margin="normal"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#04C9C8',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#04C9C8',
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={loginData.password}
            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            margin="normal"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#04C9C8',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#04C9C8',
                },
              },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={loading || !loginData.username || !loginData.password}
            startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
            sx={{ 
              mt: 4,
              mb: 2,
              background: '#04C9C8',
              borderRadius: 2,
              py: 1.5,
              boxShadow: '0 4px 15px rgba(4, 201, 200, 0.3)',
              '&:hover': {
                background: '#03B8B7',
                boxShadow: '0 6px 20px rgba(4, 201, 200, 0.4)'
              }
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'white', display: 'flex' }}>
      {/* Sidebar */}
      <Box sx={{
        width: 240,
        background: '#181C23',
        borderRight: '1px solid #23272f',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        py: 3,
        px: 2,
        minHeight: '100vh',
        position: 'relative',
        zIndex: 2
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
            <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, background: 'white', p: '5px', borderRadius: 1 }}>
              <img src={logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', letterSpacing: 1 }}>Admin</Typography>
          </Box>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Button startIcon={<PersonIcon />} onClick={() => setCurrentPage('dashboard')} sx={{ justifyContent: 'flex-start', color: currentPage === 'dashboard' ? '#04C9C8' : '#fff', fontWeight: 500, borderRadius: 2, px: 2, py: 1.5, background: currentPage === 'dashboard' ? 'rgba(4,201,200,0.12)' : 'transparent', '&:hover': { background: 'rgba(4,201,200,0.08)' } }} disableElevation fullWidth>Dashboard</Button>
            <Button startIcon={<MessageIcon />} onClick={() => setCurrentPage('chats')} sx={{ justifyContent: 'flex-start', color: currentPage === 'chats' ? '#04C9C8' : '#fff', borderRadius: 2, px: 2, py: 1.5, background: currentPage === 'chats' ? 'rgba(4,201,200,0.12)' : 'transparent', '&:hover': { background: 'rgba(4,201,200,0.08)' } }} disableElevation fullWidth>Chats</Button>
          </Stack>
        </Box>
        <Box>
          <Divider sx={{ my: 3, borderColor: '#23272f' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#23272f', color: '#fff', mr: 1 }}>{user?.username?.[0]?.toUpperCase() || 'A'}</Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>{user?.username || 'Admin'}</Typography>
              <Typography variant="caption" sx={{ color: '#bbb' }}>Administrator</Typography>
            </Box>
          </Box>
          <Button startIcon={<LogoutIcon />} sx={{ color: '#fff', fontWeight: 500, borderRadius: 2, px: 2, py: 1.5, background: 'rgba(255,255,255,0.04)', '&:hover': { background: 'rgba(255,255,255,0.08)' } }} fullWidth onClick={handleLogout}>Logout</Button>
        </Box>
      </Box>

      {/* Main Content Layout */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'white' }}>
        {/* Header Bar */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          py: 2,
          borderBottom: '1px solid #e0e7ef',
          background: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#04C9C8' }}>{user?.username?.[0]?.toUpperCase() || 'A'}</Avatar>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{user?.username || 'Admin'}</Typography>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Main Content Area */}
          <Box sx={{ flex: 1, p: 4, minWidth: 0 }}>
            {currentPage === 'dashboard' && (
              <>
                {/* Dashboard Widgets */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={6}>
                    <Card sx={{
                      p: 0,
                      borderRadius: 3,
                      boxShadow: '0 2px 12px rgba(4,201,200,0.10)',
                      background: 'linear-gradient(135deg, #04C9C8 0%, #70e1f5 100%)',
                      color: 'white',
                      overflow: 'hidden',
                      minHeight: 120,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5 }}>Total Users</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>{statistics.unique_users ?? '--'}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', width: 48, height: 48 }}><PeopleIcon /></Avatar>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Card sx={{
                      p: 0,
                      borderRadius: 3,
                      boxShadow: '0 2px 12px rgba(35,39,47,0.10)',
                      background: 'linear-gradient(135deg, #23272f 0%, #434857 100%)',
                      color: 'white',
                      overflow: 'hidden',
                      minHeight: 120,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5 }}>Total Chats</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>{statistics.total_chats ?? '--'}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.10)', color: '#04C9C8', width: 48, height: 48 }}><MessageIcon /></Avatar>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
                {/* Welcome Card */}
                <Card sx={{ mb: 4, background: '#04C9C8', color: 'white', borderRadius: 3, boxShadow: '0 4px 24px rgba(4, 201, 200, 0.12)' }}>
                  <CardContent>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>Welcome back, {user?.username || 'Admin'}!</Typography>
                    <Typography variant="body1">Here's an overview of your chat system activity and stats.</Typography>
                  </CardContent>
                </Card>
                {/* Statistics Cards, Recent Conversations, etc. will go here */}
                {/* ...existing statistics cards, controls, table, pagination... */}
              </>
            )}
            {currentPage === 'users' && <UsersPage />}
            {currentPage === 'chats' && <ChatsPage />}
            {currentPage === 'admin-users' && <UsersManagementPage />}
          </Box>
        </Box>
      </Box>

      {/* Create Admin User Dialog */}
      <Dialog
        open={showCreateAdminUser}
        onClose={() => setShowCreateAdminUser(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, background: 'white' }
        }}
      >
        <DialogTitle sx={{ background: '#04C9C8', color: 'white', fontWeight: 'bold' }}>
          Create Admin User
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Username"
            value={newAdminUserData.username}
            onChange={(e) => setNewAdminUserData({ ...newAdminUserData, username: e.target.value })}
            margin="normal"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': { borderColor: '#04C9C8' },
                '&.Mui-focused fieldset': { borderColor: '#04C9C8' },
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={newAdminUserData.password}
            onChange={(e) => setNewAdminUserData({ ...newAdminUserData, password: e.target.value })}
            margin="normal"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': { borderColor: '#04C9C8' },
                '&.Mui-focused fieldset': { borderColor: '#04C9C8' },
              },
            }}
          />
          <TextField
            fullWidth
            select
            label="Role"
            value={newAdminUserData.role}
            onChange={(e) => setNewAdminUserData({ ...newAdminUserData, role: e.target.value })}
            margin="normal"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': { borderColor: '#04C9C8' },
                '&.Mui-focused fieldset': { borderColor: '#04C9C8' },
              },
            }}
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="super_admin">Super Admin</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowCreateAdminUser(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateAdminUser}
            disabled={loading || !newAdminUserData.username || !newAdminUserData.password}
            variant="contained"
            sx={{
              background: '#04C9C8',
              '&:hover': { background: '#03B8B7' },
              borderRadius: 2
            }}
          >
            {loading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Admin User Dialog */}
      <Dialog
        open={showUpdateAdminUser}
        onClose={() => setShowUpdateAdminUser(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, background: 'white' }
        }}
      >
        <DialogTitle sx={{ background: '#04C9C8', color: 'white', fontWeight: 'bold' }}>
          Update Admin User
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Username"
            value={updateAdminUserData.username}
            onChange={(e) => setUpdateAdminUserData({ ...updateAdminUserData, username: e.target.value })}
            margin="normal"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': { borderColor: '#04C9C8' },
                '&.Mui-focused fieldset': { borderColor: '#04C9C8' },
              },
            }}
          />
          <TextField
            fullWidth
            label="Password (leave blank to keep current)"
            type="password"
            value={updateAdminUserData.password}
            onChange={(e) => setUpdateAdminUserData({ ...updateAdminUserData, password: e.target.value })}
            margin="normal"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': { borderColor: '#04C9C8' },
                '&.Mui-focused fieldset': { borderColor: '#04C9C8' },
              },
            }}
          />
          <TextField
            fullWidth
            select
            label="Role"
            value={updateAdminUserData.role}
            onChange={(e) => setUpdateAdminUserData({ ...updateAdminUserData, role: e.target.value })}
            margin="normal"
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': { borderColor: '#04C9C8' },
                '&.Mui-focused fieldset': { borderColor: '#04C9C8' },
              },
            }}
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="super_admin">Super Admin</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowUpdateAdminUser(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAdminUser}
            disabled={loading || !updateAdminUserData.username}
            variant="contained"
            sx={{
              background: '#04C9C8',
              '&:hover': { background: '#03B8B7' },
              borderRadius: 2
            }}
          >
            {loading ? 'Updating...' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat History Dialog */}
      <Dialog
        open={showChatHistory}
        onClose={() => {
          setShowChatHistory(false);
          setSelectedUserChats([]);
          setChatHistoryPage(1);
          setHasMoreChats(true);
          setChatHistoryLoading(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, background: 'white', boxShadow: '0 8px 32px rgba(4, 201, 200, 0.12)' }
        }}
      >
        <DialogTitle sx={{
          background: '#04C9C8',
          color: 'white',
          fontWeight: 'bold',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          position: 'sticky',
          top: 0,
          zIndex: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#04C9C8', width: 40, height: 40 }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                {selectedUserPhone}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Chat History
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, background: '#f7fafd', minHeight: 400 }}>
          {chatHistoryLoading && selectedUserChats.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress sx={{ color: '#04C9C8', mb: 2 }} />
              <Typography>Loading chat history...</Typography>
            </Box>
          ) : selectedUserChats.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AIIcon sx={{ fontSize: 48, color: '#04C9C8', mb: 2 }} />
              <Typography>No chat history found.</Typography>
            </Box>
          ) : (
            <Box 
              sx={{ p: 3, maxHeight: 500, overflowY: 'auto' }}
              onScroll={handleChatScroll}
            >
              {/* Show message at the beginning of conversation */}
              {selectedUserChats.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Beginning of conversation
                  </Typography>
                </Box>
              )}
              {selectedUserChats.map((chat, idx) => (
                <Box key={`${chat.id}-${idx}`} sx={{ mb: 3 }}>
                  {/* Timestamp */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(chat.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                  {/* User Message */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', mb: 1, gap: 1 }}>
                    <Box sx={{
                      maxWidth: '70%',
                      bgcolor: '#04C9C8',
                      color: 'white',
                      borderRadius: '18px 18px 4px 18px',
                      p: 2,
                      boxShadow: 1
                    }}>
                      {chat.user_audio_path ? (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>🎤 Audio Message</Typography>
                          {chat.message && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mb: 1, 
                                fontStyle: 'italic',
                                opacity: 0.9,
                                fontSize: '0.85rem'
                              }}
                            >
                              {chat.message}
                            </Typography>
                          )}
                          <AudioPlayer 
                            src={chat.user_audio_path}
                            userId={selectedUserPhone}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2">{chat.message}</Typography>
                      )}
                    </Box>
                    <Avatar sx={{ bgcolor: '#04C9C8', width: 32, height: 32 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                  </Box>
                  {/* AI Response */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1 }}>
                    <Avatar sx={{ bgcolor: '#e0e7ef', width: 32, height: 32 }} src={agentImg} />
                    <Box sx={{
                      maxWidth: '70%',
                      bgcolor: 'white',
                      color: '#222',
                      borderRadius: '18px 18px 18px 4px',
                      p: 2,
                      boxShadow: 1,
                      border: '1px solid #e0e7ef'
                    }}>
                      {chat.assistant_audio_path ? (
                        <Box>
                          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666' }}>
                            🔊 Audio Response
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mb: 1, 
                              fontStyle: 'italic',
                              opacity: 0.8,
                              fontSize: '0.85rem',
                              color: '#555'
                            }}
                          >
                            {chat.response}
                          </Typography>
                          <AudioPlayer 
                            src={chat.assistant_audio_path}
                            userId={selectedUserPhone}
                          />
                        </Box>
                      ) : (
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => (
                              <Typography variant="body2" component="p" sx={{ mb: 1 }}>
                                {children}
                              </Typography>
                            )
                          }}
                        >
                          {chat.response}
                        </ReactMarkdown>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
              
              {/* Loading indicator for newer messages */}
              {chatHistoryLoading && hasMoreChats && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} sx={{ color: '#04C9C8' }} />
                  <Typography variant="caption" sx={{ ml: 1, alignSelf: 'center' }}>
                    Loading more messages...
                  </Typography>
                </Box>
              )}
              
              {/* Show message if no more newer messages */}
              {!hasMoreChats && selectedUserChats.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    End of conversation
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;